/** ERP module URL prefixes. Pages live under the matching first segment. */
export const ERP_OPERATIONS_SLUG = 'operations';
/** Former operations module prefix — kept for redirects from old bookmarks. */
export const ERP_OPERATIONS_SLUG_LEGACY = 'hospitality';
export const ERP_SALES_SLUG = 'sales-distribution';
export const ERP_REPORTING_SLUG = 'back-office';
/** Former module prefix — kept for redirects from old bookmarks. */
export const ERP_REPORTING_SLUG_LEGACY = 'reporting-analytics';
export const ERP_CRM_SLUG = 'crm';

export const ERP_OPERATIONS_BASE = `/${ERP_OPERATIONS_SLUG}`;
export const ERP_SALES_BASE = `/${ERP_SALES_SLUG}`;
export const ERP_REPORTING_BASE = `/${ERP_REPORTING_SLUG}`;
export const ERP_CRM_BASE = `/${ERP_CRM_SLUG}`;

export type ErpModuleId =
  | 'operations'
  | 'sales_distribution'
  | 'reporting_analytics'
  | 'crm';

/** Rooms / operations page slug under the operations module. */
export const OPERATIONS_SLUG = 'operations';

const joinModulePath = (
  base: string,
  ...segments: Array<string | number | null | undefined>
) => {
  const rest = segments
    .filter((s) => s !== null && s !== undefined && String(s).length > 0)
    .map((s) => String(s).replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/');
  return rest ? `${base}/${rest}` : base;
};

export const operationsModulePath = (
  ...segments: Array<string | number | null | undefined>
) => joinModulePath(ERP_OPERATIONS_BASE, ...segments);

export const salesModulePath = (
  ...segments: Array<string | number | null | undefined>
) => joinModulePath(ERP_SALES_BASE, ...segments);

export const reportingModulePath = (
  ...segments: Array<string | number | null | undefined>
) => joinModulePath(ERP_REPORTING_BASE, ...segments);

export const crmModulePath = (
  ...segments: Array<string | number | null | undefined>
) => joinModulePath(ERP_CRM_BASE, ...segments);

export const ERP_MODULE_BASE: Record<ErpModuleId, string> = {
  operations: ERP_OPERATIONS_BASE,
  sales_distribution: ERP_SALES_BASE,
  reporting_analytics: ERP_REPORTING_BASE,
  crm: ERP_CRM_BASE,
};

export const ERP_MODULE_IDS: ErpModuleId[] = [
  'operations',
  'sales_distribution',
  'reporting_analytics',
  'crm',
];

export const erpModulePath = (
  moduleId: ErpModuleId,
  ...segments: Array<string | number | null | undefined>
) => joinModulePath(ERP_MODULE_BASE[moduleId], ...segments);

/** Settings URL under a given ERP module (`/back-office/settings/account`). */
export const erpSettingsPath = (
  moduleId: ErpModuleId,
  ...segments: Array<string | number | null | undefined>
) => erpModulePath(moduleId, 'settings', ...segments);

/** All `/<module>/settings` route patterns for Routing. */
export const erpSettingsRoutePaths = (): string[] => (
  ERP_MODULE_IDS.flatMap((id) => [
    `${erpSettingsPath(id)}/:subtab/:plan`,
    `${erpSettingsPath(id)}/:subtab`,
    erpSettingsPath(id),
  ])
);

export const ERP_PATHS = {
  operations: operationsModulePath(OPERATIONS_SLUG),
  operationsActions: (id: string | number) => (
    operationsModulePath(OPERATIONS_SLUG, 'actions', id)
  ),
  dashboard: operationsModulePath('dashboard'),
  sales: salesModulePath('sales'),
  refunds: salesModulePath('refunds'),
  reservations: operationsModulePath('reservations'),
  walkIns: operationsModulePath('walk-ins'),
  rooms: operationsModulePath('rooms'),
  roomsTab: (subtab: string) => operationsModulePath('rooms', subtab),
  roomsEditRoom: (id: string | number) => (
    operationsModulePath('rooms', 'editroom', id)
  ),
  departments: salesModulePath('departments'),
  departmentsSub: (subtab: string) => salesModulePath('departments', subtab),
  inventory: salesModulePath('inventory'),
  activityLogs: reportingModulePath('logs'),
  activityLogsAction: (action: string | number) => reportingModulePath('logs', action),
  metrics: reportingModulePath('metrics'),
  commandCenter: reportingModulePath('command-center'),
  forms: reportingModulePath('forms'),
  formsToken: (token: string) => reportingModulePath('forms', 'workflow', token),
  formsWorkflow: (token: string) => reportingModulePath('forms', 'workflow', token),
  formsReport: (id: string) => reportingModulePath('forms', 'report', id),
  formsReportRequest: (id: string) => reportingModulePath('forms', 'report-request', id),
  reporting: reportingModulePath('reporting'),
  reportingTab: (subtab: string) => reportingModulePath('reporting', subtab),
  compliance: reportingModulePath('compliance'),
  complianceTab: (subtab: string) => reportingModulePath('compliance', subtab),
  orgDepartments: reportingModulePath('departments'),
  orgDepartmentsSub: (subtab: string) => reportingModulePath('departments', subtab),
  workflow: reportingModulePath('workflow'),
  workflowTab: (subtab: string) => reportingModulePath('workflow', subtab),
  records: reportingModulePath('records'),
  recordsKind: (kind: string) => reportingModulePath('records', kind),
  recordsItem: (kind: string, id: string, action?: string) => (
    reportingModulePath('records', kind, id, action)
  ),
  crm: ERP_CRM_BASE,
  crmTab: (tab: string) => crmModulePath(tab),
  crmMessage: (tab: string, messageId: string) => (
    crmModulePath(tab, messageId)
  ),
  settings: operationsModulePath('settings'),
  settingsSub: (subtab: string) => operationsModulePath('settings', subtab),
  settingsPlan: (subtab: string, plan: string) => (
    operationsModulePath('settings', subtab, plan)
  ),
  /** Edit-room lives under Room setup, not Settings. */
  settingsEditRoom: (id: string | number) => (
    operationsModulePath('rooms', 'editroom', id)
  ),
  settingsAccount: operationsModulePath('settings', 'account'),
  reserve: operationsModulePath('reserve'),
};

const isOperationsModuleRoot = (root: string | undefined): boolean => (
  root === ERP_OPERATIONS_SLUG || root === ERP_OPERATIONS_SLUG_LEGACY
);

export const operationsPageSlug = (pathname: string): string => {
  const parts = pathname.split('/').filter(Boolean);
  if (!isOperationsModuleRoot(parts[0])) return '';
  return parts[1] || '';
};

/** Second URL segment for any ERP module (`/crm/feedback` → `feedback`). */
export const erpPageSlug = (pathname: string): string => {
  const parts = pathname.split('/').filter(Boolean);
  return parts[1] || '';
};

export const getErpModuleFromPath = (pathname: string): ErpModuleId | null => {
  const root = pathname.split('/').filter(Boolean)[0];
  if (isOperationsModuleRoot(root)) return 'operations';
  if (root === ERP_SALES_SLUG) return 'sales_distribution';
  if (root === ERP_REPORTING_SLUG || root === ERP_REPORTING_SLUG_LEGACY) return 'reporting_analytics';
  if (root === ERP_CRM_SLUG) return 'crm';
  return null;
};

/** Settings URL that keeps the module of the current pathname. */
export const erpSettingsPathFromPathname = (
  pathname: string,
  ...segments: Array<string | number | null | undefined>
) => erpSettingsPath(getErpModuleFromPath(pathname) || 'reporting_analytics', ...segments);

export const isOperationsModulePath = (pathname: string): boolean => (
  operationsPageSlug(pathname) !== '' || pathname === ERP_OPERATIONS_BASE
    || pathname.startsWith(`${ERP_OPERATIONS_BASE}/`)
);

export const isErpAppPath = (pathname: string): boolean => (
  getErpModuleFromPath(pathname) !== null
);

/** True for operations-module pages and nested segments. */
export const isOperationsPagePath = (pathname: string, page: string): boolean => (
  operationsPageSlug(pathname) === page
);

/** True for `/sales-distribution/departments` and nested segments. */
export const isErpPagePath = (pathname: string, page: string): boolean => (
  erpPageSlug(pathname) === page
);

/** Exact operations page with no extra segments. */
export const isExactOperationsPage = (pathname: string, page: string): boolean => {
  const parts = pathname.split('/').filter(Boolean);
  return isOperationsModuleRoot(parts[0]) && parts[1] === page && parts.length === 2;
};

/** Exact ERP page regardless of module prefix (`/back-office/logs`). */
export const isExactErpPage = (pathname: string, page: string): boolean => {
  const parts = pathname.split('/').filter(Boolean);
  return parts[1] === page && parts.length === 2;
};

/** Current slug plus former /logs URLs. */
export const isActivityLogsPagePath = (pathname: string): boolean => (
  isErpPagePath(pathname, 'logs') || isErpPagePath(pathname, 'audits')
);

export const isExactActivityLogsPage = (pathname: string): boolean => (
  isExactErpPage(pathname, 'logs') || isExactErpPage(pathname, 'audits')
);

export const isErpSettingsAccount = (pathname: string): boolean => {
  const parts = pathname.split('/').filter(Boolean);
  return getErpModuleFromPath(pathname) !== null
    && parts[1] === 'settings'
    && parts[2] === 'account';
};
